import { MENUS } from '../constants/menus';

// Flatten all menu items with links
const getAllMenuItems = () => {
  return MENUS.flatMap((menu) => menu.subMenus || []);
};

/**
 * Extract base path from a route path by removing param segments.
 * React Router v5 path formats:
 * - Static: /users, /dashboard
 * - Dynamic params: /users/:id, /orders/:orderId
 * - Optional params: /books/:bookId/:page?
 * - Wildcards: /files/*
 * - Multiple params: /users/:userId/posts/:postId
 *
 * This function strips dynamic segments (starting with ':') from the path.
 *
 * @param {string} path - The route path to extract base from
 * @returns {string} The base path without dynamic segments
 */
export const getBasePath = (path) => {
  if (!path) return '';

  // Extract everything before the first ':' or '*'
  const basePath = path.match(/^[^:*]*/)?.[0] || '/';

  // Remove trailing slash (unless it's root '/')
  return basePath.length > 1 && basePath.endsWith('/')
    ? basePath.slice(0, -1)
    : basePath;
};

/**
 * Get permission config for a given route path.
 * Matching rules:
 * - /routes/:id -> matches /routes (route with params matches base menu link)
 * - /routes -> matches /routes (exact match)
 * - /routes/subpath -> does NOT match /routes (sub-paths don't match)
 */
export const getPermissionConfig = (routePath) => {
  const allItems = getAllMenuItems();
  const basePath = getBasePath(routePath);

  // Find menu item that exactly matches the base path
  const matchedItem = allItems.find((item) => {
    return item.link === basePath;
  });

  return matchedItem
    ? { link: matchedItem.link, scopes: matchedItem.scopes, actionPermission: matchedItem.actionPermission }
    : { scopes: [], actionPermission: null };
};

export const hasScopeAndActionPermission = ({ scopes = [], actionPermission = null, userScopes = [], permissionStore }) => {
  // Check scopes - if scopes are defined, user must have at least one matching scope
  const hasScopePermission = scopes.length === 0 || scopes.some((s) => userScopes.includes(s));

  // Check action permission - if actionPermission is defined, check if the action is not denied
  const hasActionPermission = !actionPermission || !permissionStore?.isDenied(actionPermission);

  return hasScopePermission && hasActionPermission;
};
